import React, { FC, useState, ReactNode, useEffect, useRef } from "react";
import { CollapsibleSection, CollapsibleSectionProps } from "common/components/CollapsibleSection";
import styled from "@emotion/styled";
import { ThemeProps } from "common/components/ui";

const StyledCollapsibleSection = styled(CollapsibleSection)<ThemeProps>(
  ({ theme }) => `
    background-color: transparent;
    
    .collapseHeader {
      background-color: transparent;
      justify-content: flex-start;
      padding-left: ${theme.spacing.S7};
      padding-top: 0;
      padding-bottom: 0;
    }
  `
);

interface CustomCollapsibleSectionProps extends Omit<CollapsibleSectionProps, "collapsedContent"> {
  expandedText: ReactNode;
  collapsedText: ReactNode;
}

export const CustomCollapsibleSection: FC<CustomCollapsibleSectionProps> = ({
  expandedText,
  collapsedText,
  isDefaultOpen = false,
  children,
  ...restProps
}) => {
  const [isExpanded, setIsExpanded] = useState(isDefaultOpen);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize with default state
  useEffect(() => {
    setIsExpanded(isDefaultOpen);
  }, [isDefaultOpen]);

  // Monitor for DOM changes to detect when the section expands/collapses
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (containerRef.current) {
        // Look for expanded section element which indicates it's expanded
        const expandedSection = containerRef.current.querySelector('[class*="expandedSection"]');
        setIsExpanded(!!expandedSection);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef}>
      <StyledCollapsibleSection
        {...restProps}
        isDefaultOpen={isDefaultOpen}
        collapsedContent={<div>{isExpanded ? expandedText : collapsedText}</div>}
      >
        {children}
      </StyledCollapsibleSection>
    </div>
  );
};
